import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface StepStatus {
  completed: boolean;
  label: string;
}

interface CloudGatewayTutorialLauncherProps {
  onStartTutorial: () => void;
  onDismiss: () => void;
  currentStep?: number;
  totalSteps?: number;
  isVisible?: boolean;
  stepsStatus?: StepStatus[];
}

const defaultSteps: StepStatus[] = [
  { label: 'Add an AI provider', completed: false },
  { label: 'Create a repository', completed: false },
  { label: 'Generate an API key', completed: false },
  { label: 'Make your first request', completed: false },
];

const CloudGatewayTutorialLauncher: React.FC<CloudGatewayTutorialLauncherProps> = ({
  onStartTutorial,
  onDismiss,
  currentStep = 0,
  totalSteps = 4,
  isVisible = true,
  stepsStatus = defaultSteps,
}) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [showPulse, setShowPulse] = useState(true);

  // Stop pulse animation after first interaction
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  const progress = currentStep > 0 ? (currentStep / totalSteps) * 100 : 0;
  const isInProgress = currentStep > 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '80px',
        right: '24px',
        zIndex: 1000,
        animation: showPulse ? 'pulse 2s infinite' : 'none',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
          '100%': { transform: 'scale(1)' },
        },
      }}
    >
      {isMinimized ? (
        // Minimized state - just an icon button
        <Box
          onClick={() => setIsMinimized(false)}
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(97, 123, 255, 0.4)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 6px 24px rgba(97, 123, 255, 0.5)',
            },
          }}
        >
          <SchoolIcon sx={{ fontSize: 28, color: 'white' }} />
          {isInProgress && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
              }}
            >
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'white' }}>
                {currentStep}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Expanded state - full card
        <Box
          sx={{
            width: 320,
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
          }}
        >
          {/* Header with gradient */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SchoolIcon sx={{ fontSize: 24, color: 'white' }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'white' }}>
                Gateway Tutorial
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(true)}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <Box sx={{ width: 14, height: 2, bgcolor: 'currentColor', borderRadius: 1 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={onDismiss}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            {isInProgress ? (
              // In progress state
              <>
                <Typography sx={{ fontSize: '13px', color: '#535862', mb: 2 }}>
                  Continue your tutorial journey
                </Typography>
                
                {/* Progress bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>Progress</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#617BFF' }}>
                      {currentStep}/{totalSteps}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, bgcolor: '#E5E7EB', borderRadius: 3 }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${progress}%`,
                        background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
                        borderRadius: 3,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={onStartTutorial}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    bgcolor: '#617BFF',
                    fontWeight: 600,
                    py: 1,
                    '&:hover': { bgcolor: '#5068e0' },
                  }}
                >
                  Resume Tutorial
                </Button>
              </>
            ) : (
              // Not started state
              <>
                <Typography sx={{ fontSize: '13px', color: '#535862', mb: 2, lineHeight: 1.6 }}>
                  Learn how to set up Flashgate with our interactive guide.
                </Typography>

                {/* Steps preview */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                  {stepsStatus.map((step, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {step.completed ? (
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#10B981' }} />
                      ) : idx === currentStep ? (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#617BFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'white',
                          }}
                        >
                          {idx + 1}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#F3F4F6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#9CA3AF',
                          }}
                        >
                          {idx + 1}
                        </Box>
                      )}
                      <Typography 
                        sx={{ 
                          fontSize: '12px', 
                          color: step.completed ? '#10B981' : idx === currentStep ? '#617BFF' : '#535862',
                          fontWeight: step.completed || idx === currentStep ? 600 : 400,
                          textDecoration: step.completed ? 'line-through' : 'none',
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={onDismiss}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: '10px',
                      borderColor: '#D5D7DA',
                      color: '#535862',
                      fontWeight: 600,
                      fontSize: '13px',
                      '&:hover': { borderColor: '#617BFF', bgcolor: 'rgba(97, 123, 255, 0.04)' },
                    }}
                  >
                    Later
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={onStartTutorial}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: '10px',
                      bgcolor: '#617BFF',
                      fontWeight: 600,
                      fontSize: '13px',
                      '&:hover': { bgcolor: '#5068e0' },
                    }}
                  >
                    Start
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CloudGatewayTutorialLauncher;
